import {
  useraccountDetailsRequest,
  fetchRewardAccountRequest,
} from '@rb-serve-app/core';
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SecureLS from 'secure-ls';

export enum EventType {
  PAGE_VIEW,
  CLICK_EVENT,
}

interface ErrorsType {
  category: string;
  type: string;
  code: string;
  API: string;
  field: string;
  message: string;
}

interface AnalyticsContextType {
  trackEvent: (
    eventType: EventType,
    pageName: string[] | string,
    url: string,
    interactionName?: string,
    isSelfServe?: boolean,
    selfServe?: Record<string, string>,
    errors?: ErrorsType,
    isSignInPage?: boolean,
    miscellaneous?: Record<string, string>,
    isSelfServeForClick?: boolean,
    isSearchKey?: boolean,
    search?: Record<string, string>,
    isSelfServeStart?: boolean,
    isSigninEvent?: boolean,
    isSuccess?: boolean
  ) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined
);

export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const ls = new SecureLS({ encodingType: 'aes' });
  const {
    customerId,
    accountId,
    accountDetails,
    error,
    loading,
    userCredentials,
  } = useSelector((state: any) => state.user);

  const hasFetchedAccountDetails = useRef(false);

  useEffect(() => {
    if (
      accountDetails == null &&
      error == null &&
      loading == false &&
      !hasFetchedAccountDetails.current
    ) {
      if (accountId && customerId) {
        hasFetchedAccountDetails.current = true;
        console.log('Dispatching useraccountDetailsRequest');
        dispatch(useraccountDetailsRequest({ accountId, customerId }));
      }
      //dispatch(fetchRewardAccountRequest({ user_id: accountId.replace(/^0+/, '') }));
    }
  }, []);

  const constructData = (pageName: string[] | string, url: string) => {
    return {
      account: constructAccountData(),
      applicationName: 'rogersbank|rb-serve-app.git',
      flowName: 'Cross-Platform',
      page: constructPageData(pageName, url),
      personalization: {},
      site: {
        name: 'RogersBank',
      },
    };
  };
  const constructAccountData = () => {
    return {
      internalaccountID: accountDetails?.accountId || 'accountId',
      username:
        ls.get('user') && JSON.parse(ls.get('user'))?.userCredentials?.username,
      type: accountDetails?.productName || "Customer's Account Type",
      customerID:
        accountDetails?.customer?.customerId || "Customer's Account Type",
    };
  };
  const constructPageData = (pageName: string[] | string, url: string) => {
    let hierarchy = ['cross-platform'];
    let pageNameFiltered;
    if (typeof pageName === 'object') {
      pageNameFiltered = pageName[0];
      hierarchy = [...hierarchy, pageName[1]];
    } else {
      pageNameFiltered = pageName;
    }
    return {
      name: pageNameFiltered,
      hierarchy: hierarchy,
      language: 'en',
      province: 'ON',
      url,
    };
  };

  const constructPageViewData = (pageName: string[] | string, url: string) => {
    return {
      ...constructData(pageName, url),
      events: {
        pageView: true,
      },
    };
  };

  const constructClickEventData = (
    pageName: string[] | string,
    url: string,
    interactionName: string
  ) => {
    return {
      ...constructData(pageName, url),
      events: {
        interaction: true,
      },
      interaction: {
        type: 'click',
        name: interactionName,
      },
    };
  };

  const trackEvent = (
    eventType: EventType,
    pageName: string[] | string,
    url: string,
    interactionName?: string,
    isSelfServe = false,
    selfServe: Record<string, string> = {},
    errors = {} as ErrorsType,
    isSignInPage = false,
    miscellaneous: Record<string, string> = {},
    isSelfServeForClick = false,
    isSearchKey = false,
    search: Record<string, string> = {},
    isSelfServeStart = false,
    isSigninEvent = false,
    isSuccess = false
  ) => {
    let trackData;
    if (eventType === EventType.PAGE_VIEW) {
      trackData = constructPageViewData(pageName, url);
      if (isSelfServe) {
        trackData = {
          ...trackData,
          selfServe,
          events: {
            pageView: true,
            selfServeComplete: true,
          },
        };
      }
      if (isSignInPage) {
        trackData = {
          ...trackData,
          miscellaneous,
          events: {
            pageview: true,
            miscellaneous: true,
          },
        };
      }
      if (isSuccess) {
        trackData = {
          ...trackData,
          events: {
            signinSuccess: true,
          },
        };
      }

      if (errors && Object.values(errors).length) {
        trackData = {
          ...trackData,
          events: {
            errors: true,
          },
          errors: [
            {
              category: errors?.category,
              type: errors?.type,
              code: errors?.code,
              API: errors?.API,
              field: errors?.field,
              message: errors?.message,
            },
          ],
        };
      }
      console.log('Construction', trackData);
    } else {
      if (interactionName) {
        console.log('Construction', interactionName);
        trackData = constructClickEventData(pageName, url, interactionName);
        if (isSelfServeForClick) {
          trackData = {
            ...trackData,
            selfServe,
            events: {
              interaction: true,
              selfServeComplete: true,
            },
          };
        }
        if (isSearchKey) {
          trackData = {
            ...trackData,
            search,
            events: {
              search: true,
            },
          };
        }
        if (isSelfServeStart) {
          trackData = {
            ...trackData,
            selfServe,
            events: {
              interaction: true,
              selfServeStart: true,
            },
          };
        }
        if (isSigninEvent) {
          trackData = {
            ...trackData,
            events: {
              interaction: true,
              signinAttempt: true,
            },
          };
        }
      }
    }
    if (window._trackData) {
      window._trackData(trackData);
    }
  };
  return (
    <AnalyticsContext.Provider value={{ trackEvent }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
